import { i18n } from '@/i18n';
import { computed, ref, StyleValue } from 'vue';
import { FieldItem, ValidateRule } from '@/coreDomainsModule/constant/validateRules';
import { useForms } from '@/corePagesModule/composables/useForm';
import { waitLoading } from '@/corePagesModule/components/InfoModal/waitLoading';
import { commonModalTextMapper, formOptionsAggregateBusiness, formOptionsBusiness, inputTypeKeyMap } from '../constants';
import { showInfoModal } from '@/corePagesModule/components/InfoModal/InfoModalManager';
import { ExistsMasterDataResponseData } from '@/coreInfrastructureModule/axios/generated/registerBusinessManage';
import { diContainer } from '@/coreContainer/container';

/**
 * AddOptionのEmitsタイプ
 */
type AddOptionSectionEmit = (e: 'setOption', type: string, name: string, code?: string) => void;

/**
 * useAddOptionSectionのオプション
 */
type AddOptionSectionOption = {
  isAggregateBusiness?: boolean;
  customOption?: string[];
};

/**
 * AddOptionを使用するためのComposable
 * @param emits - emits
 * @param option - オプション
 */
export const useAddOptionSection = (emits: AddOptionSectionEmit, option?: AddOptionSectionOption) => {
  const t = i18n.t;

  const VALUE_ONLY_OPTIONS = computed(() => [t('package'), t('manufacturer'), t('endUser')]);

  /**
   * 表示する選択肢の数を決定するための変数
   */
  const isAnotherInputShow = ref(false);
  const itemType = ref('');
  const optionFieldCount = computed(() => {
    return isAnotherInputShow.value ? 2 : 1;
  });

  /**
   * フォーム用の選択肢項目定義
   */
  const newOptionFieldItems = computed((): FieldItem[] => {
    const targetArray = ['value', 'option', 'anotherValue', 'anotherOption'];
    const items = targetArray.map((name: string) => {
      return {
        name,
        rules: [ValidateRule.Required],
      };
    });
    return items;
  });

  /**
   * バリデーションエラー発生時にテキスト/ボタンの位置を調整するためのスタイル
   */
  const textStyle = computed(
    (): StyleValue => (Object.values(fieldState).some((state) => state.isError.value) ? { paddingTop: '12px' } : {})
  );
  const buttonStyle = computed(
    (): StyleValue => (Object.values(fieldState).some((state) => state.isError.value) ? { marginTop: '12px' } : {})
  );

  /**
   * フォーム変数
   */
  const { fieldState, allValidate, resetForm } = useForms(newOptionFieldItems.value);

  /**
   * 新規選択肢追加処理で、追加ボタンが有効かどうか
   */
  const isAddButtonActive = computed(() => {
    if (VALUE_ONLY_OPTIONS.value.includes(fieldState['option'].value.value)) {
      return fieldState['value'].value.value && fieldState['option'].value.value;
    }
    return (
      fieldState['value'].value.value &&
      fieldState['option'].value.value &&
      fieldState['anotherValue'].value.value &&
      fieldState['anotherOption'].value.value
    );
  });

  /**
   * 項目内容の新規追加時に、対となる項目が存在する場合にそれを返す関数
   * @param item - 現在追加している項目
   * @returns - 対となる項目、ない場合はundefined
   */
  const getAnotherAddingItem = (item: string) => {
    if (item === t('package') || item === t('manufacturer') || item === t('endUser')) return undefined;
    switch (item) {
      case t('warehouseNumber'):
        return t('warehouseName');
      case t('warehouseName'):
        return t('warehouseNumber');
      case t('supplierNumber'):
        return t('supplierName');
      case t('supplierName'):
        return t('supplierNumber');
      case t('businessNumber'):
        return t('businessName');
      case t('businessName'):
        return t('businessNumber');
      case t('customerNumber'):
        return t('customerName');
      case t('customerName'):
        return t('customerNumber');
      case t('salesDestinationNumber'):
        return t('salesDestinationName');
      case t('salesDestinationName'):
        return t('salesDestinationNumber');
    }
  };

  /**
   * 新規選択肢追加で追加する項目を重複チェックAPI用に変換する関数
   * @param option - 追加する項目
   * @returns - 何を重複チェックするかのparameter
   */
  const convertNewOptionToItemType = (option: string) => {
    switch (option) {
      case t('warehouseNumber'):
      case t('warehouseName'):
        return 'warehouse';
      case t('supplierNumber'):
      case t('supplierName'):
        return 'supplier';
      case t('businessNumber'):
      case t('businessName'):
        return 'item';
      case t('package'):
        return 'package';
      case t('manufacturer'):
        return 'manufacturer';
      case t('customerNumber'):
      case t('customerName'):
        return 'customer';
      case t('salesDestinationNumber'):
      case t('salesDestinationName'):
        return 'salesDestination';
      case t('endUser'):
        return 'endUser';
      default:
        return '';
    }
  };

  /**
   * 新規選択肢追加のinputに入力した際の処理
   * @param e - 変更された値
   * @param kind - どのinputに入力されたか
   */
  const updateInput = (e: string, kind: 'value' | 'option' | 'anotherValue') => {
    if (kind === 'option') {
      if (e === fieldState['option'].value.value) return;
      itemType.value = convertNewOptionToItemType(e);
      if (!e || VALUE_ONLY_OPTIONS.value.includes(e)) {
        isAnotherInputShow.value = false;
        const value = fieldState['value'].value.value;
        resetForm(newOptionFieldItems.value);
        fieldState['value'].setValue(value);
        fieldState['option'].setValue(e);
      } else {
        const value = fieldState['value'].value.value;
        resetForm(newOptionFieldItems.value);
        fieldState['value'].setValue(value);
        fieldState['option'].setValue(e);
        fieldState['anotherOption'].setValue(getAnotherAddingItem(e) ?? '');
        isAnotherInputShow.value = true;
      }
    } else {
      fieldState[kind].setValue(e);
    }
  };

  const AddableOptions = computed(() => {
    if (option?.customOption && option?.customOption.length > 0) return option?.customOption;
    const targetFormOptions = option?.isAggregateBusiness ? formOptionsAggregateBusiness : formOptionsBusiness;
    const res = [...targetFormOptions].filter((option) => {
      return inputTypeKeyMap[option] === 'select';
    });

    return res.map((r) => t(r));
  });

  /**
   * 新規選択肢追加ボタンを押した際の処理
   */
  const addOption = async () => {
    /**
     * テキストがundefinedだった場合に空文字に変換する関数
     * @param text - テキスト
     */
    const _checkUndefinedText = (text?: string) => {
      return text ? t(text) : '';
    };

    const validateResult = VALUE_ONLY_OPTIONS.value.includes(fieldState['option'].value.value)
      ? fieldState['value'].validate(fieldState['value'].value.value).isValid
      : allValidate();
    if (!validateResult) return;

    let value: string, code: string;
    let isAnotherValueCode: boolean;
    if (
      [
        t('warehouseName'),
        t('supplierName'),
        t('businessName'),
        t('package'),
        t('customerName'),
        t('salesDestinationName'),
        t('manufacturer'),
        t('endUser'),
      ].includes(fieldState['option'].value.value)
    ) {
      isAnotherValueCode = true;
      value = fieldState['value'].value.value;
      code = fieldState['anotherValue'].value.value;
    } else {
      isAnotherValueCode = false;
      value = fieldState['anotherValue'].value.value;
      code = fieldState['value'].value.value;
    }
    const checkDuplicationResponse = await waitLoading(
      checkDuplicationOfNewOption(value, code),
      _checkUndefinedText(commonModalTextMapper.newOption.loading?.title)
    );
    if (!checkDuplicationResponse) {
      await showInfoModal({
        modalTitle: _checkUndefinedText(commonModalTextMapper.newOption.commonError?.title),
        modalDescription: _checkUndefinedText(commonModalTextMapper.newOption.commonError?.description),
        backButtonLabel: t('確認'),
        icon: 'error',
        colorStyle: 'error',
        enableOutsideClick: false,
        isDisabledNextButton: true,
      });
      return;
    }
    if (checkDuplicationResponse.existsResult) {
      await showInfoModal({
        modalTitle: _checkUndefinedText(commonModalTextMapper.newOption.duplicateError?.title),
        modalDescription: _checkUndefinedText(commonModalTextMapper.newOption.duplicateError?.description),
        backButtonLabel: t('確認'),
        icon: 'error',
        colorStyle: 'error',
        enableOutsideClick: false,
        isDisabledNextButton: true,
      });
      // 重複チェックに引っかかった場合
      if (checkDuplicationResponse.existsValue.toString() === value) {
        fieldState[isAnotherValueCode ? 'value' : 'anotherValue'].isError.value = true;
        fieldState[isAnotherValueCode ? 'value' : 'anotherValue'].errorMessage.value = t('重複しています');
      }
      if (checkDuplicationResponse.existsCode === code) {
        fieldState[isAnotherValueCode ? 'anotherValue' : 'value'].isError.value = true;
        fieldState[isAnotherValueCode ? 'anotherValue' : 'value'].errorMessage.value = t('重複しています');
      }
    } else {
      const newOptionItemType = itemType.value === 'item' ? 'business' : itemType.value;
      emits('setOption', newOptionItemType, value, code);
      resetForm(newOptionFieldItems.value);
    }
    return;
  };

  /**
   * 新規に追加しようとする選択肢が重複していないかを確認する処理
   * ManufacturerとEndUserは重複チェックしない
   * @param value - 新規追加するvalue(warehouseName, supplierName, etc...)
   * @param code - 新規追加するCode(warehouseNumber, supplierNumber, etc...)
   */
  const checkDuplicationOfNewOption = async (value: string, code: string): Promise<ExistsMasterDataResponseData | null> => {
    if (itemType.value === 'manufacturer' || itemType.value === 'endUser') {
      // ManufacturerとEndUserは重複チェックせず、existsResultをfalseにして返す
      return {
        existsResult: false,
        existsCode: '',
        existsValue: '',
      };
    }
    if (!itemType.value) {
      console.error('エラー:取引編集>新規選択肢追加:追加したい項目が存在しません');
      return null;
    }
    const response = await diContainer.checkNewOptionDuplicateUseCase.execute(itemType.value, code, value);
    switch (response.status.type) {
      case 'success':
        return response.status.data;
      case 'error':
      default:
        console.error('エラー:取引編集>新規選択肢追加:サーバーエラー');
        return null;
    }
  };

  return {
    fieldState,
    isAnotherInputShow,
    optionFieldCount,
    isAddButtonActive,
    textStyle,
    buttonStyle,
    AddableOptions,
    updateInput,
    addOption,
  };
};
